//@ts-check
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/app/guards/ProtectedRoute.jsx';
import { RoleRoute } from '@/app/guards/RoleRoute.jsx';
import { AppShell } from '@/components/layout/AppShell.jsx';

const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage.jsx'));
const HomePage = lazy(() => import('@/features/dashboard/pages/HomePage.jsx'));
const RecordPage = lazy(() => import('@/features/record/pages/RecordPage.jsx'));
const VideosPage = lazy(() => import('@/features/videos/pages/VideosPage.jsx'));
const VideoPlayerPage = lazy(() => import('@/features/videos/pages/VideoPlayerPage.jsx'));
const PublicSharePage = lazy(() => import('@/features/videos/pages/PublicSharePage.jsx'));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage.jsx'));
const MarketplacesPage = lazy(() => import('@/features/marketplaces/pages/MarketplacesPage.jsx'));
const UsersPage = lazy(() => import('@/features/users/pages/UsersPage.jsx'));
const SiteSettingsPage = lazy(() => import('@/features/settings/pages/SiteSettingsPage.jsx'));
const StorageSettingsPage = lazy(() => import('@/features/settings/pages/StorageSettingsPage.jsx'));
const DataRetentionPage = lazy(() => import('@/features/settings/pages/DataRetentionPage.jsx'));

/**
 * @returns {import('react').ReactElement}
 */
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-page text-muted-foreground">
      Loading…
    </div>
  );
}

/**
 * @param {{ children: import('react').ReactNode }} props
 */
function withSuspense({ children }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense({ children: <LoginPage /> }),
  },
  {
    path: '/share/:token',
    element: withSuspense({ children: <PublicSharePage /> }),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            element: <RoleRoute adminOnly />,
            children: [
              { path: '/', element: withSuspense({ children: <HomePage /> }) },
              { path: '/reports', element: withSuspense({ children: <ReportsPage /> }) },
              {
                path: '/marketplaces',
                element: withSuspense({ children: <MarketplacesPage /> }),
              },
              { path: '/users', element: withSuspense({ children: <UsersPage /> }) },
              {
                path: '/admin/storage',
                element: withSuspense({ children: <StorageSettingsPage /> }),
              },
              {
                path: '/admin/data-retention',
                element: withSuspense({ children: <DataRetentionPage /> }),
              },
              {
                path: '/admin/site-settings',
                element: withSuspense({ children: <SiteSettingsPage /> }),
              },
            ],
          },
          {
            element: <RoleRoute permission="record" />,
            children: [{ path: '/record', element: withSuspense({ children: <RecordPage /> }) }],
          },
          {
            element: <RoleRoute permission="view" />,
            children: [
              { path: '/videos', element: withSuspense({ children: <VideosPage /> }) },
              { path: '/videos/:id', element: withSuspense({ children: <VideoPlayerPage /> }) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
